import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Switch,
  Paper,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import AppService from '../../../Configurations/Appservice';
import API_ENDPOINTS from '../../../Api/endpoints';

const EMPTY_FORM = {
  id: null,
  gatewayKey: '',
  displayName: '',
  feePercentage: '0',
  feeFixed: '0',
  enabled: true,
};

/**
 * Simple admin table for payment-gateway platform-fee configuration.
 * Gateways are addable rows; each carries a percentage + fixed platform fee and an
 * enabled flag. API keys/secrets stay in the server's application.properties — they
 * are intentionally NOT managed here.
 */
const GatewayListPage = () => {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(form.id);

  useEffect(() => {
    loadGateways();
  }, []);

  const loadGateways = async () => {
    try {
      setLoading(true);
      const data = await AppService.get(API_ENDPOINTS.PAYMENT_GATEWAYS.LIST);
      setGateways(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError('Failed to load payment gateways');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setForm({
      id: g.id,
      gatewayKey: g.gatewayKey || '',
      displayName: g.displayName || '',
      feePercentage: g.feePercentage != null ? String(g.feePercentage) : '0',
      feeFixed: g.feeFixed != null ? String(g.feeFixed) : '0',
      enabled: g.enabled !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.gatewayKey.trim()) {
      setError('Gateway key is required');
      return;
    }
    const payload = {
      gatewayKey: form.gatewayKey.trim().toUpperCase(),
      displayName: form.displayName,
      feePercentage: parseFloat(form.feePercentage) || 0,
      feeFixed: parseFloat(form.feeFixed) || 0,
      enabled: form.enabled,
    };
    try {
      setSaving(true);
      if (isEdit) {
        await AppService.put(API_ENDPOINTS.PAYMENT_GATEWAYS.UPDATE(form.id), payload);
      } else {
        await AppService.post(API_ENDPOINTS.PAYMENT_GATEWAYS.CREATE, payload);
      }
      setDialogOpen(false);
      await loadGateways();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save gateway');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (g) => {
    try {
      await AppService.put(API_ENDPOINTS.PAYMENT_GATEWAYS.TOGGLE_STATUS(g.id));
      await loadGateways();
    } catch (err) {
      setError('Failed to toggle status');
    }
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete gateway "${g.gatewayKey}"?`)) return;
    try {
      await AppService.delete(API_ENDPOINTS.PAYMENT_GATEWAYS.GET(g.id));
      await loadGateways();
    } catch (err) {
      setError('Failed to delete gateway');
    }
  };

  const fmt = (v) => (v != null ? Number(v).toFixed(2) : '0.00');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Payment Gateways
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Set the platform fee (percentage + fixed) and enable/disable online payment per gateway.
            API keys are configured on the server, not here.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add Gateway
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Gateway Key</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell align="right">Fee %</TableCell>
                  <TableCell align="right">Fixed Fee (AED)</TableCell>
                  <TableCell align="center">Enabled</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                  </TableRow>
                ) : gateways.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                        No gateways configured yet. Click "Add Gateway" to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  gateways.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell><Typography fontWeight="medium">{g.gatewayKey}</Typography></TableCell>
                      <TableCell>{g.displayName || '-'}</TableCell>
                      <TableCell align="right">{fmt(g.feePercentage)}%</TableCell>
                      <TableCell align="right">{fmt(g.feeFixed)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={g.enabled ? 'Disable online payment' : 'Enable online payment'}>
                          <Switch checked={g.enabled !== false} onChange={() => handleToggleStatus(g)} color="success" />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(g)}><EditIcon /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(g)}><DeleteIcon /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? 'Edit Gateway' : 'Add Gateway'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Gateway Key"
              value={form.gatewayKey}
              onChange={(e) => setForm({ ...form, gatewayKey: e.target.value })}
              disabled={isEdit}
              helperText="Must match the backend gateway code (e.g. NGENIUS)"
              fullWidth
            />
            <TextField
              label="Display Name"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Fee Percentage (%)"
              type="number"
              value={form.feePercentage}
              onChange={(e) => setForm({ ...form, feePercentage: e.target.value })}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Fixed Fee (AED)"
              type="number"
              value={form.feeFixed}
              onChange={(e) => setForm({ ...form, feeFixed: e.target.value })}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />}
              label="Enabled (allow online payment through this gateway)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GatewayListPage;
